
import React, { useState, useContext } from 'react';
import ChartBase, { useChartBase, humanizeNumber } from './chart-base';
import TweenedNumber from 'core/ui/tweened-number';
import SessionContext from 'SessionContext';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Alert, Input } from 'reactstrap';
import LabelWithTooltip from './label-with-tooltip';

function IntRangeWidget(props) {
  const context = useContext(SessionContext);
  const base = useChartBase(props);
  const [modal, setModal] = useState(false);
  const [inputType] = useState(props.inputType || "Number");
  const [startValue, setStartValue] = useState("");
  const [endValue, setEndValue] = useState("");
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // use child methods if provided
  const chartRenderer = props.renderChart
    ? () => props.renderChart({ ...props, base })
    : () => renderChart({...props, base});

  const handleInputChange = props.onInputChange
    ? (ev, type) => props.onInputChange(ev, type, setStartValue, setEndValue, setError, setErrorMessage)
    : onInputChange;

  const classNames = 'count-widget geoboundary-chart';

  // Reset modal state on open/close
  const toggle = () => {
    setModal(!modal);
    setStartValue("");
    setEndValue("");
    setError(false);
    setErrorMessage("");
  };

  // Helper: add leading zeros to make full width of uiType
  function makeFixedWidthValue(val) {
    if (!props.fixedWidth) return val;
    const width = props.fixedWidth;
    val = val.toString();
    while (val.length < width) val = "0" + val;
    return val;
  }

  // Helper: sum values in range
  function getRangeValueSum(filter, allValues) {
    let start = parseFloat(filter.value.split('|')[0]);
    let end = parseFloat(filter.value.split('|')[1]);
    let sum = 0;
    for (const [key, value] of Object.entries(allValues)) {
      if (key >= start && key <= end) {
        sum += value;
      }
    }
    return sum;
  }

  // Get values for display
  function getValues() {
    const config = props.config;
    const filters = props.filters || [];
    const value_series = config.ds + '.' + config.id;
    const _values = props.values[value_series] || {};
    const values = filters.map((filter) => [filter.friendly_name || filter.value, getRangeValueSum(filter, _values) || 0]);
    values.sort((a, b) => b[1] - a[1]);
    return values;
  }

  // Validate range input
  function validateRange(start = startValue, end = endValue) {
    if (!start || !end) {
      setError(true);
      setErrorMessage("Fields cannot be empty.");
      return false;
    }
    if (parseFloat(start) > parseFloat(end)) {
      setError(true);
      setErrorMessage("Start Value cannot be greater than End Value.");
      return false;
    } else if (parseFloat(end) < parseFloat(start)) {
      setError(true);
      setErrorMessage("End Value cannot be less than Start Value.");
      return false;
    }
    setError(false);
    setErrorMessage("");
    return true;
  }

  // Input change handler
  function onInputChange(ev, type) {
    ev.preventDefault && ev.preventDefault();
    setError(false);
    setErrorMessage("");
    const value = ev.target.value;
    // Int only
    const validated = value.match(/^-?\d*$/g);
    if (validated || value === "") {
      if (type === 'start') setStartValue(value);
      if (type === 'end') setEndValue(value);
    }
  }

  // Commit range filter
  function onRangeCommit() {
    const validated = validateRange();
    if (validated) {
      const start = makeFixedWidthValue(startValue);
      const end = makeFixedWidthValue(endValue);
      const chart = props.config;
      const range = start + '|' + end;
      context.crossfilterStore.add_filter(chart.id, chart.id, range, false);
      toggle();
      props.onChartUpdate && props.onChartUpdate();
    }
  }

  // Remove range filter
  function onRemoveRange(ev) {
    ev.preventDefault();
    const chart = props.config;
    const entries = getValues().filter((value, index, arr) => {
      return value[0] !== ev.currentTarget.id;
    });
    const new_filters = entries.map((entry) => ({ series: chart.id, value: entry[0], isNegated: false }));
    context.crossfilterStore.set_filters(chart.id, new_filters);
    props.onChartUpdate && props.onChartUpdate();
  }

  // Format display range
  function formatDisplayRange(value) {
    if (!value) return;
    const range = value.split("|");
    if(range.length === 1) {
      return value;
    }
    let start = parseFloat(range[0]);
    let end = parseFloat(range[1]);
    return start + " to " + end;
  }

  // Render summary for ChartBaseF
  base.renderSummary = () => {
    const values = getValues();
    return <ul>{values.map(([value]) => <li key={value}>{formatDisplayRange(value)}</li>)}</ul>;
  };

  // Main chart content
  function renderChart({base, ...props}) {
    const chartType = props.config.label;
    const shouldTween = props.tween;
    const values = getValues();
    const viewOnly = props.viewOnly;
    return (
      <>
        {!viewOnly && (
          <>
            <Button size="sm" className="btn-light" onClick={toggle}>
              + Add {inputType} Range
            </Button>
            <Modal isOpen={modal} toggle={toggle} className="modal-dialog-centered date-picker">
              <ModalHeader toggle={toggle}>{chartType}</ModalHeader>
              <ModalBody>
                <div className="input-range">
                  <div className="ir-start">
                    <label>Start Value</label>
                    <Input
                      value={startValue}
                      onChange={(ev) => handleInputChange(ev, 'start')}
                      max={endValue}
                    />
                  </div>
                  <i className="bi bi-arrow-right"></i>
                  <div className="ir-end">
                    <label>End Value</label>
                    <Input
                      value={endValue}
                      onChange={(ev) => handleInputChange(ev, 'end')}
                      max={endValue}
                    />
                  </div>
                </div>
                <Alert color="warning" fade isOpen={error}>
                  <i className="bi bi-exclamation-diamond"></i>
                  {errorMessage}
                </Alert>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" size="sm" onClick={toggle}>
                  Cancel
                </Button>
                <Button color="primary" className="promote" size="sm" onClick={onRangeCommit}>
                  Add {inputType} Range
                </Button>
              </ModalFooter>
            </Modal>
          </>
        )}
        {values.length === 0 && <p>No Filters Selected</p>}
        {values.map(([value, count]) => (
          <div className="value" key={value}>
            <LabelWithTooltip label={formatDisplayRange(value)} parentProps={props} />
            <i className="spacer"></i>
            {shouldTween && <TweenedNumber className="count" value={count} />}
            {!shouldTween && <span className="count">{humanizeNumber(count)}</span>}
            {!viewOnly && (
              <Button color="link" size="sm" className="remove" id={value} onClick={onRemoveRange}>
                <i className="bi bi-trash"></i>
              </Button>
            )}
          </div>
        ))}
      </>
    );
  }

  // Render using ChartBaseF
  return (
    <ChartBase {...props} base={base} chartClassName={classNames}>
      <div className={classNames}>
        {chartRenderer()}
      </div>
    </ChartBase>
  );
}

export default IntRangeWidget;
